import { setup, assign, assertEvent } from "xstate";
import { MENU_IDS } from "@design-lab/features/weave-design-lab/model/state";

export type UrlFlagsContext = {
  workflowFlags: {
    hasDesignLibrary: boolean;
    hasCanva: boolean;
    hasAiDesign: boolean;
    hasStartDesigning: boolean;
  };

  // Track which workflows have been consumed/handled
  consumedWorkflows: Set<string>;

  transientData: {
    artworkUrls: string | undefined;
    uploadUrls: string | undefined; // New: Support for multiple side uploads
    fileContentType: string | undefined;
    catalogNumber: string | undefined;
    activeComposition: string | undefined;
    side: string | undefined;
    source: string | undefined;
    pdpQuantities: string | undefined;
    pdpSizeData: string | undefined;
    enhancementsStarted: boolean | undefined;
    dtfChannelUuid: string | undefined;
  };

  isInitialized: boolean;
};

export const urlFlagsMachine = setup({
  types: {
    context: {} as UrlFlagsContext,
    events: {} as
      | { type: "params.initialize"; searchParams: URLSearchParams }
      | { type: "params.consume.transient" }
      | { type: "workflow.consumed"; workflow: keyof UrlFlagsContext["workflowFlags"] }
      | { type: "params.clear.all" }
      | { type: "reset" }
  },
  actions: {
    captureInitialParams: assign(({ event, context }) => {
      if (event.type !== "params.initialize") return context;

      const { searchParams } = event;

      // Only set flags if they haven't been consumed yet
      const newWorkflowFlags = {
        hasDesignLibrary:
          !context.consumedWorkflows.has("hasDesignLibrary") &&
          searchParams.get("design-library") === "true",
        hasCanva:
          !context.consumedWorkflows.has("hasCanva") && searchParams.get("canva") === "true",
        hasAiDesign:
          !context.consumedWorkflows.has("hasAiDesign") &&
          searchParams.get("design-menu") === MENU_IDS.AI_DESIGN,
        hasStartDesigning:
          !context.consumedWorkflows.has("hasStartDesigning") &&
          searchParams.get("start-designing") === "true"
      };

      return {
        ...context,
        isInitialized: true,
        workflowFlags: newWorkflowFlags,
        transientData: {
          artworkUrls: searchParams.get("artwork_urls") || undefined,
          uploadUrls: searchParams.get("upload_urls") || undefined,
          fileContentType: searchParams.get("file_content_type") || undefined,
          catalogNumber: searchParams.get("catalog_number") || undefined,
          activeComposition: searchParams.get("ac") || undefined,
          side: searchParams.get("side") || undefined,
          source: searchParams.get("source") || undefined,
          pdpQuantities: searchParams.get("pdp_quantities") || undefined,
          pdpSizeData: searchParams.get("pdp_size_data") || undefined,
          enhancementsStarted: searchParams.get("enhancements_started") === "true" || undefined,
          dtfChannelUuid: searchParams.get("dtf_channel_uuid") || undefined
        }
      };
    }),

    markWorkflowConsumed: assign(({ event, context }) => {
      assertEvent(event, "workflow.consumed");

      const newConsumedWorkflows = new Set(context.consumedWorkflows);
      newConsumedWorkflows.add(event.workflow);

      return {
        ...context,
        consumedWorkflows: newConsumedWorkflows,
        workflowFlags: {
          ...context.workflowFlags,
          [event.workflow]: false // Immediately set to false when consumed
        }
      };
    }),

    clearTransientData: assign(({ context }) => ({
      ...context,
      transientData: {
        artworkUrls: undefined,
        uploadUrls: undefined,
        fileContentType: undefined,
        catalogNumber: undefined,
        activeComposition: undefined,
        side: undefined,
        source: undefined,
        pdpQuantities: undefined,
        pdpSizeData: undefined,
        enhancementsStarted: undefined,
        dtfChannelUuid: undefined
      }
    })),

    resetAll: assign(() => ({
      workflowFlags: {
        hasDesignLibrary: false,
        hasCanva: false,
        hasAiDesign: false,
        hasStartDesigning: false
      },
      consumedWorkflows: new Set(),
      transientData: {
        artworkUrls: undefined,
        uploadUrls: undefined,
        fileContentType: undefined,
        catalogNumber: undefined,
        activeComposition: undefined,
        side: undefined,
        source: undefined,
        pdpQuantities: undefined,
        pdpSizeData: undefined,
        enhancementsStarted: undefined,
        dtfChannelUuid: undefined
      },
      isInitialized: false
    }))
  }
}).createMachine({
  id: "urlFlags",
  context: () => ({
    workflowFlags: {
      hasDesignLibrary: false,
      hasCanva: false,
      hasAiDesign: false,
      hasStartDesigning: false
    },
    consumedWorkflows: new Set<string>(),
    transientData: {
      artworkUrls: undefined,
      uploadUrls: undefined,
      fileContentType: undefined,
      catalogNumber: undefined,
      activeComposition: undefined,
      side: undefined,
      source: undefined,
      pdpQuantities: undefined,
      pdpSizeData: undefined,
      enhancementsStarted: undefined,
      dtfChannelUuid: undefined
    },
    isInitialized: false
  }),
  initial: "uninitialized",
  states: {
    uninitialized: {
      on: {
        "params.initialize": {
          target: "initialized",
          actions: "captureInitialParams"
        }
      }
    },

    initialized: {
      on: {
        "workflow.consumed": {
          actions: ["markWorkflowConsumed"],
          description: "Mark workflow as handled to prevent re-opening"
        },

        "params.consume.transient": {
          actions: "clearTransientData"
        },

        reset: {
          target: "uninitialized",
          actions: "resetAll"
        }
      }
    }
  }
});
